import React from "react";
import { View, Dimensions } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import Skeletons from "../skeleton-components/Skeletons";

const { width: screenWidth } = Dimensions.get("window");

const MyBeltsSkeleton: React.FC = () => {
  const deviceSize = useDeviceSize();
  const styles = useStyles();

  // Simulate 4 belt placeholders
  const beltPlaceholders = Array(4).fill(null);

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          flex: 1,
          margin:
            deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
              ? 1
              : 0,
          padding:
            deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
              ? 12
              : 24,
          borderColor: AppColors.gray_828282,
          borderWidth: 0.8,
          borderRadius: 8,
          backgroundColor: AppColors.black_191919,
        }}
      >
        {/* Parent View with Two Columns */}
        <View
          style={{
            flexDirection: "row",
            gap: 24,
            alignItems: "stretch",
          }}
        >
          {/* Column 1: Belts Section */}
          <View style={{ flex: 1, flexDirection: "column" }}>
            {/* Header Section */}
            <View
              style={{
                flexDirection:
                  deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                    ? "column"
                    : "row",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
              }}
            >
              <View
                style={{
                  width:
                    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                      ? "100%"
                      : "auto",
                }}
              >
                <Skeletons
                  width={
                    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                      ? "150px"
                      : "200px"
                  }
                  height={24}
                  style={{ borderRadius: 4 }}
                />
              </View>
              <View
                style={{
                  flexDirection: "row",
                  ...(deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? { width: "100%", justifyContent: "space-between" }
                    : { gap: 16 }),
                }}
              >
                <Skeletons
                  width={
                    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                      ? "140px"
                      : "160px"
                  }
                  height={34}
                  style={{ borderRadius: 6 }}
                />
                <Skeletons
                  width={
                    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                      ? "100px"
                      : "105px"
                  }
                  height={32}
                  style={{ borderRadius: 6 }}
                />
              </View>
            </View>

            {/* Separator */}
            <Skeletons
              width="100%"
              height={1}
              style={{ marginTop: 12, marginBottom: 12, borderRadius: 4 }}
            />

            {/* Belt Row - Single Row */}
            <View
              style={{
                flexDirection: "row",
                gap: 12,
                flex: 1,
              }}
            >
              {beltPlaceholders.map((_, index) => (
                <View
                  key={index}
                  style={{
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <View style={{ flex: 1, width: "100%", minHeight: 200 }}>
                    <View
                      style={{
                        flex: 1,
                        borderRadius: 8,
                        borderWidth: 0.8,
                        borderColor: AppColors.gray_828282,
                        marginBottom: 12,
                        alignItems: "center",
                        justifyContent: "center",
                        paddingVertical: 20,
                        paddingHorizontal: 8,
                        paddingBottom: 56,
                        gap: 12,
                        backgroundColor: AppColors.black_191919,
                      }}
                    >
                      <Skeletons
                        width="80px"
                        height={14}
                        style={{ borderRadius: 4 }}
                      />
                      <Skeletons
                        width="70px"
                        height="70px"
                        style={{ borderRadius: 8 }}
                      />
                      <View
                        style={{
                          flexDirection: "row",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <Skeletons
                          width="40px"
                          height={12}
                          style={{ borderRadius: 4 }}
                        />
                        <Skeletons
                          width="50px"
                          height={16}
                          style={{ borderRadius: 4 }}
                        />
                      </View>
                    </View>
                    <View
                      style={{
                        backgroundColor: AppColors.black,
                        borderRadius: 5,
                        padding: 6,
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 12,
                        marginHorizontal: 4,
                      }}
                    >
                      <Skeletons
                        width="50px"
                        height={18}
                        style={{ borderRadius: 4, alignSelf: "center" }}
                      />
                    </View>
                  </View>
                  <Skeletons
                    width="100%"
                    height={34}
                    style={{ borderRadius: 10 }}
                  />
                </View>
              ))}
            </View>
          </View>

          {/* Column 2: Diamond Achievement Section - Only show on larger screens */}
          {deviceSize !== DEVICE_SIZES.XS &&
            deviceSize !== DEVICE_SIZES.SM &&
            deviceSize !== DEVICE_SIZES.MD && (
              <View style={{ minWidth: 200, maxWidth: 220 }}>
                <View
                  style={{
                    padding: 16,
                    flex: 1,
                    borderRadius: 6,
                    borderWidth: 0.8,
                    borderColor: AppColors.gray_828282,
                    backgroundColor: AppColors.black_191919,
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                  }}
                >
                  <Skeletons
                    width="160px"
                    height={32}
                    style={{ borderRadius: 4 }}
                  />
                  <Skeletons
                    width="90px"
                    height="90px"
                    style={{ borderRadius: 8 }}
                  />
                  <Skeletons
                    width="100px"
                    height={20}
                    style={{ borderRadius: 4 }}
                  />
                  <View style={{ gap: 6, alignItems: "center" }}>
                    <Skeletons
                      width="90px"
                      height={12}
                      style={{ borderRadius: 4 }}
                    />
                    <Skeletons
                      width="140px"
                      height={20}
                      style={{ borderRadius: 4 }}
                    />
                    <Skeletons
                      width="130px"
                      height={28}
                      style={{ borderRadius: 4 }}
                    />
                  </View>
                </View>
              </View>
            )}
        </View>
      </View>
    </View>
  );
};

export default MyBeltsSkeleton;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
